import React, { useState } from "react";
import { Reveal } from "../Reveal";
import { CONTACT } from "./SocialLinks";
import portraet from "../../assets/portraet.webp";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputBase =
  "w-full bg-transparent border-0 border-b-2 border-black/10 py-3 text-[17px] text-[#111827] placeholder-[#111827]/30 focus:border-[#852936] focus:outline-none focus:ring-0 transition-colors duration-300";

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-10 6L2 7" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const PreviewContact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact/submit`, formData);
      setSubmitStatus({ type: "success", message: response.data.message });
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      setSubmitStatus({ type: "error", message: "Der opstod en fejl. Prøv venligst igen." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden bg-[#F4F1EC] py-16 sm:py-24" data-testid="preview-section-contact">
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[460px] w-[460px] rounded-full bg-[#A9B5A0]/18 blur-[140px]" />
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          {/* Left: intro + contact details */}
          <div>
            <Reveal>
              <div className="mb-7 h-0.5 w-12 bg-[#852936]" />
              <h2
                className="font-display text-4xl font-semibold leading-[1.02] tracking-tight text-[#852936] sm:text-5xl md:text-6xl"
                data-testid="preview-heading-contact"
              >
                Lad os tale sammen
              </h2>
            </Reveal>

            <Reveal delay={100}>
              <div className="mt-8 space-y-4">
                {CONTACT.emails.map((mail) => (
                  <a
                    key={mail}
                    href={`mailto:${mail}`}
                    className="group flex items-center gap-4 text-[#111827]/80 transition-colors hover:text-[#852936]"
                    data-testid={`preview-contact-email-${mail}`}
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#A9B5A0]/15 text-[#852936]">
                      <MailIcon />
                    </span>
                    <span className="text-[16px]">{mail}</span>
                  </a>
                ))}
                <a
                  href={CONTACT.phoneHref}
                  className="group flex items-center gap-4 text-[#111827]/80 transition-colors hover:text-[#852936]"
                  data-testid="preview-contact-phone-link"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#A9B5A0]/15 text-[#852936]">
                    <PhoneIcon />
                  </span>
                  <span className="text-[16px]">{CONTACT.phone}</span>
                </a>
              </div>

              <div className="mt-10">
                <img
                  src={portraet}
                  alt="Stine Eskesen"
                  className="w-full rounded-3xl preview-shadow-soft"
                  data-testid="preview-contact-image"
                />
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <div>
            <Reveal delay={120}>
              <form
                onSubmit={handleSubmit}
                className="space-y-7 rounded-3xl border border-black/[0.06] bg-white p-6 sm:p-10 preview-shadow-soft"
                data-testid="preview-contact-form"
              >
                <div>
                  <label htmlFor="p-name" className="mb-1 block text-sm font-medium text-[#111827]/60">
                    Navn
                  </label>
                  <input
                    id="p-name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    data-testid="preview-contact-name-input"
                    className={inputBase}
                  />
                </div>
                <div>
                  <label htmlFor="p-email" className="mb-1 block text-sm font-medium text-[#111827]/60">
                    E-mail
                  </label>
                  <input
                    id="p-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    data-testid="preview-contact-email-input"
                    className={inputBase}
                  />
                </div>
                <div>
                  <label htmlFor="p-phone" className="mb-1 block text-sm font-medium text-[#111827]/60">
                    Telefon
                  </label>
                  <input
                    id="p-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    data-testid="preview-contact-phone-input"
                    className={inputBase}
                  />
                </div>
                <div>
                  <label htmlFor="p-message" className="mb-1 block text-sm font-medium text-[#111827]/60">
                    Besked
                  </label>
                  <textarea
                    id="p-message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    required
                    placeholder="Skriv din besked her..."
                    data-testid="preview-contact-message-input"
                    className={`${inputBase} resize-none`}
                  />
                </div>

                {submitStatus && (
                  <div
                    data-testid="preview-contact-status"
                    className={`rounded-2xl p-4 text-[15px] ${
                      submitStatus.type === "success"
                        ? "border border-[#A9B5A0] bg-[#A9B5A0]/20 text-[#111827]"
                        : "border border-red-200 bg-red-50 text-red-800"
                    }`}
                  >
                    {submitStatus.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="preview-contact-submit-button"
                  className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#852936] px-9 py-4 text-lg font-medium text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 preview-shadow-soft"
                >
                  {isSubmitting && (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  )}
                  {isSubmitting ? "Sender..." : "Send besked"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PreviewContact;
